
import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TextField, Select, MenuItem, Button, Box, Paper, Fade, Collapse, IconButton, Tooltip, Chip, Stack } from '@mui/material';

import { updateFilters } from '../../redux/balancePerAccountSlice';
import { loadAccount } from '../../redux/financeAccountSlice';
import { loadCategory } from '../../redux/categorySlice';
import { FormControl, InputLabel } from '@mui/material';
import { dateToString } from '../../shared/formatters';

const FilterContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
}));

const FilterHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
    },
}));

const ToggleButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: 'rgb(0,98,105)',
    color: 'white',
    boxShadow: '0 4px 16px rgba(0, 98, 105, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: 'rgb(0,85,91)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 98, 105, 0.4)',
    },
    [theme.breakpoints.down('sm')]: {
        flex: 1,
    },
}));

const QuickActionButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: 'white',
    color: theme.palette.text.primary,
    border: '2px solid #e0e0e0',
    width: '48px',
    height: '48px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        borderColor: 'rgb(0,98,105)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
}));

const CollapsibleWrapper = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    zIndex: 10,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        position: 'relative',
        top: 'auto',
        marginTop: theme.spacing(2),
    },
}));

const FilterPanel = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    marginTop: theme.spacing(2),
}));

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
    }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: '180px',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        minWidth: 'unset',
        width: '100%',
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: theme.palette.background.paper,
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    minHeight: '48px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginTop: theme.spacing(1),
    }
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        width: '100%',
    }
}));

const FilterPillsContainer = styled(Stack)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1),
    minHeight: '32px',
}));

const FilterPill = styled(Chip)(({ theme }) => ({
    borderRadius: '20px',
    backgroundColor: 'rgba(0, 98, 105, 0.1)',
    color: 'rgb(0, 98, 105)',
    fontWeight: 500,
    border: '1px solid rgba(0, 98, 105, 0.3)',
    height: 'auto',
    '& .MuiChip-deleteIcon': {
        color: 'rgba(0, 98, 105, 0.6)',
        '&:hover': {
            color: 'rgb(0, 98, 105)',
        },
    },
}));

function submitSearch(event, searchFilters, updateFilters, setIsFilterOpen) {
    event.preventDefault();
    updateFilters(searchFilters);
    setIsFilterOpen(false);
};

function updateUserFilter(updateSearchFilters, searchFilters, users, accounts) {
    const financeAccounts = (accounts || []).filter(account => users.includes(account.user.id)).map(account => account.id);

    updateSearchFilters({ ...searchFilters, users, financeAccounts });
};

function resetFilters(event, updateFilters, updateSearchFilters, setIsFilterOpen) {
    event.preventDefault();
    var today = new Date();
    const searchFilters = {
        financeAccounts: [],
        users: [],
        to: dateToString(new Date(today.getFullYear(), today.getMonth() + 1, 0))
    };
    updateSearchFilters(searchFilters);
    updateFilters(searchFilters);
    setIsFilterOpen(false);
}

function getFilterPills(searchFilters, accounts, categories, users) {
    const pills = [];

    if (searchFilters.to) {
        pills.push({
            id: 'date-to',
            label: `Até: ${searchFilters.to}`,
            type: 'dateTo',
            value: searchFilters.to
        });
    }

    searchFilters.users.forEach(userId => {
        const user = (users || []).find(u => u.id === userId);
        if (user) {
            pills.push({
                id: `user-${userId}`,
                label: `Responsável: ${user.name}`,
                type: 'user',
                value: userId
            });
        }
    });

    searchFilters.financeAccounts.forEach(accountId => {
        const account = (accounts || []).find(a => a.id === accountId);
        if (account) {
            pills.push({
                id: `account-${accountId}`,
                label: `Conta: ${account.name}`,
                type: 'account',
                value: accountId
            });
        }
    });

    return pills;
}

function handleRemoveFilterPill(pill, searchFilters, updateSearchFilters, updateFiltersAction) {
    let updatedFilters = { ...searchFilters };
    
    switch (pill.type) {
        case 'dateTo':
            var today = new Date();
            updatedFilters = {
                ...updatedFilters,
                to: dateToString(new Date(today.getFullYear(), today.getMonth() + 1, 0))
            };
            break;
        case 'user':
            updatedFilters = {
                ...updatedFilters,
                users: updatedFilters.users.filter(u => u !== pill.value),
                financeAccounts: updatedFilters.financeAccounts
            };
            break;
        case 'account':
            updatedFilters = {
                ...updatedFilters,
                financeAccounts: updatedFilters.financeAccounts.filter(a => a !== pill.value)
            };
            break;
        default:
            break;
    }
    
    updateSearchFilters(updatedFilters);
    updateFiltersAction(updatedFilters);
}

function Filter(props) {
    const dispatch = useDispatch();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    
    // Redux state selectors
    const filterFields = useSelector(state => state.balancePerAccount.filterFields);
    const accounts = useSelector(state => state.financeAccount.items);
    const categories = useSelector(state => state.category.items);
    const users = useSelector(state => state.account.users);
    const onExport = props.onExport;
    
    // Redux actions
    const loadAccounts = useCallback(() => dispatch(loadAccount()), [dispatch]);
    const loadCategories = useCallback(() => dispatch(loadCategory()), [dispatch]);
    const updateFiltersAction = (filters) => dispatch(updateFilters(filters));

    const [searchFilters, updateSearchFilters] = useState({ ...filterFields });

    const filterPills = getFilterPills(searchFilters, accounts, categories, users);
    const hasActiveFilters = filterPills.length > 0;

    useEffect(() => {
        if ((accounts || []).length === 0) {
            loadAccounts();
        }
        if ((categories || []).length === 0) {
            loadCategories();
        }
    }, [accounts, categories, loadAccounts, loadCategories]);

    const filterForm = (
        <Fade in={true} timeout={300}>
            <FilterPanel elevation={0}>
                <StyledForm onSubmit={event => submitSearch(event, searchFilters, updateFiltersAction, setIsFilterOpen)}>
                    <StyledFormControl>
                        <StyledTextField
                            id="to"
                            name="to"
                            label="Data"
                            type="date"
                            variant="outlined"
                            value={searchFilters.to}
                            onChange={event => updateSearchFilters({ ...searchFilters, to: event.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="users-label">Responsável</InputLabel>
                        <StyledSelect
                            labelId="users-label"
                            id="userId"
                            multiple
                            value={searchFilters.users}
                            label="Responsável"
                            onChange={event => updateUserFilter(updateSearchFilters, searchFilters, event.target.value, accounts)}
                        >
                            {(users || []).map(user => <MenuItem key={user.id} value={user.id}>{`${user.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="accounts-label">Conta</InputLabel>
                        <StyledSelect
                            labelId="accounts-label"
                            id="accounts"
                            multiple
                            value={searchFilters.financeAccounts}
                            label="Conta"
                            onChange={event => updateSearchFilters({ ...searchFilters, financeAccounts: event.target.value })}
                        >
                            {(accounts || []).map(account => <MenuItem key={account.id} value={account.id}>{`${account.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>
                    
                    <ButtonGroup>
                        <PrimaryButton
                            type="submit"
                            variant="contained"
                            startIcon={<FilterListIcon />}
                        >
                            Filtrar
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={event => resetFilters(event, updateFilters, updateSearchFilters, setIsFilterOpen)}
                        >
                            Limpar
                        </SecondaryButton>
                    </ButtonGroup>
                </StyledForm>
            </FilterPanel>
        </Fade>
    );

    return (
        <FilterContainer>
            <FilterHeader>
                <ToggleButton
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    startIcon={<FilterListIcon />}
                    endIcon={isFilterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {isFilterOpen ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </ToggleButton>
                <Tooltip title="Limpar Filtros" placement="top">
                    <QuickActionButton
                        onClick={event => resetFilters(event, updateFiltersAction, updateSearchFilters, setIsFilterOpen)}
                    >
                        <ClearIcon />
                    </QuickActionButton>
                </Tooltip>
                <Tooltip title="Exportar" placement="top">
                    <QuickActionButton
                        onClick={event => onExport()}
                    >
                        <FileDownloadIcon />
                    </QuickActionButton>
                </Tooltip>
            </FilterHeader>
            
            {hasActiveFilters && (
                <FilterPillsContainer role="region" aria-label="Applied filters">
                    {filterPills.map(pill => (
                        <FilterPill
                            key={pill.id}
                            label={pill.label}
                            onDelete={() => handleRemoveFilterPill(pill, searchFilters, updateSearchFilters, updateFiltersAction)}
                            sx={{
                                py: 1.5,
                                px: 1.5,
                            }}
                        />
                    ))}
                </FilterPillsContainer>
            )}
            
            <CollapsibleWrapper>
                <Collapse in={isFilterOpen} timeout={300}>
                    {filterForm}
                </Collapse>
            </CollapsibleWrapper>
        </FilterContainer>
    );
}

export default Filter;